<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { X } from 'lucide-vue-next';
import { SearchNavIcon } from '@/nav/lucideNavIcons';
import { useLocalPageSearch } from '@/composables/useLocalPageSearch';
import type { LocalPageSearchGroup } from '@/lib/localPageSearch';

const props = defineProps<{
  placeholder: string;
  /** When false, clears query and hits (panel closed). */
  searchOpen: boolean;
  /** Active UI / route language (BCP 47) for primary link preference. */
  currentLocale: string;
}>();

const emit = defineEmits<{
  select: [payload: { url: string; locale: string }];
}>();

const { t } = useI18n();
const { hits, isSearching, scheduleSearch, cancelAndClear } = useLocalPageSearch();

const uid = useId();
const inputId = `${uid}-nav-local-search-input`;
const listboxId = `${uid}-nav-local-search-listbox`;

const inputRef = ref<HTMLInputElement | null>(null);
const query = ref('');
const highlightedIndex = ref(-1);

const trimmedQuery = computed(() => query.value.trim());
const showPanel = computed(() => trimmedQuery.value.length > 0);

const activeDescendantId = computed(() => {
  if (highlightedIndex.value < 0 || highlightedIndex.value >= hits.value.length) return undefined;
  return `${uid}-nav-local-search-opt-${highlightedIndex.value}`;
});

watch(
  () => props.searchOpen,
  (open) => {
    if (!open) {
      query.value = '';
      highlightedIndex.value = -1;
      cancelAndClear();
    }
  }
);

watch(hits, (list) => {
  if (highlightedIndex.value >= list.length) {
    highlightedIndex.value = list.length > 0 ? list.length - 1 : -1;
  }
});

watch(
  () => [query.value, props.currentLocale] as const,
  ([q]) => {
    scheduleSearch(q, props.currentLocale);
    highlightedIndex.value = -1;
  }
);

function onSelectHit(url: string, locale: string): void {
  emit('select', { url, locale });
}

function onClearQuery(): void {
  query.value = '';
  highlightedIndex.value = -1;
  cancelAndClear();
  inputRef.value?.focus();
}

function onRowActivate(group: LocalPageSearchGroup): void {
  onSelectHit(group.primaryUrl, group.primaryLocale);
}

function onKeydown(e: KeyboardEvent): void {
  if (!showPanel.value) return;
  const len = hits.value.length;
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    if (len === 0) return;
    highlightedIndex.value = highlightedIndex.value < len - 1 ? highlightedIndex.value + 1 : 0;
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    if (len === 0) return;
    highlightedIndex.value = highlightedIndex.value > 0 ? highlightedIndex.value - 1 : len - 1;
  } else if (e.key === 'Enter') {
    if (len === 0) return;
    e.preventDefault();
    const i = highlightedIndex.value >= 0 ? highlightedIndex.value : 0;
    const group = hits.value[i];
    if (group) onRowActivate(group);
  }
}

defineExpose({
  inputRef,
});
</script>

<template>
  <form class="side-nav__search-form" role="search" @submit.prevent>
    <div class="side-nav__search-field">
      <component :is="SearchNavIcon" class="side-nav__search-field-icon" aria-hidden="true" />
      <input
        :id="inputId"
        ref="inputRef"
        v-model="query"
        type="search"
        class="side-nav__search-input"
        tabindex="0"
        :placeholder="placeholder"
        autocomplete="off"
        role="combobox"
        :aria-expanded="showPanel"
        :aria-controls="showPanel ? listboxId : undefined"
        aria-autocomplete="list"
        :aria-activedescendant="showPanel ? activeDescendantId : undefined"
        :aria-busy="isSearching"
        @keydown="onKeydown"
      >
      <button
        v-if="trimmedQuery.length > 0"
        type="button"
        class="side-nav__search-clear"
        :aria-label="t('nav.searchClear')"
        @click="onClearQuery"
      >
        <X class="side-nav__search-clear-icon" aria-hidden="true" />
      </button>
      <div
        v-if="showPanel"
        :id="listboxId"
        class="side-nav__search-dropdown"
        role="listbox"
        :aria-label="t('nav.searchResultsList')"
      >
        <div v-if="isSearching" class="side-nav__search-dropdown-message">
          {{ t('nav.searchLoading') }}
        </div>
        <template v-else-if="hits.length > 0">
          <div
            v-for="(group, index) in hits"
            :id="`${uid}-nav-local-search-opt-${index}`"
            :key="group.groupKey"
            role="option"
            class="side-nav__search-hit"
            :class="{ 'side-nav__search-hit--active': highlightedIndex === index }"
            :aria-selected="highlightedIndex === index"
            @click="onRowActivate(group)"
            @mouseenter="highlightedIndex = index"
          >
            <span class="side-nav__search-hit-title">{{ group.title }}</span>
            <div class="side-nav__search-hit-lang-stack" @click.stop>
              <a
                v-for="tr in group.translations"
                :key="tr.locale"
                class="side-nav__search-hit-lang-link"
                :href="tr.url"
                :aria-label="t('nav.searchGoToLocale', { locale: tr.locale.toUpperCase() })"
                @click.prevent="onSelectHit(tr.url, tr.locale)"
              >
                {{ tr.locale }}
              </a>
            </div>
          </div>
        </template>
        <div v-else class="side-nav__search-dropdown-message">
          {{ t('nav.searchNoResults') }}
        </div>
      </div>
    </div>
  </form>
</template>
